import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, X, Truck, IndianRupee, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Phase 22b — TransportPOAckModal (stage: `transport_po_ack`).
 *
 * Fields captured by Shivmohan.V on this action:
 *   • po_trip_type_confirmed (One Way | Round Trip)
 *   • po_amount (free text, mandatory)
 *   • po_amount_confirmed (checkbox, must be true)
 *   • po_remark (optional free text)
 *   • po_remark_confirmed (checkbox, must be true)
 *
 * Submit button stays disabled until trip type is picked, amount is
 * non-empty, and BOTH confirmation checkboxes are ticked.
 */
export default function TransportPOAckModal({ open, onClose, onDone, requisition, actingAsBanner = null }) {
  const [tripType, setTripType] = useState("");
  const [amount, setAmount] = useState("");
  const [amtOk, setAmtOk] = useState(false);
  const [remark, setRemark] = useState("");
  const [rOk, setROk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const seed = (requisition?.form_data?.tripType === "Round Trip") ? "Round Trip" : "One Way";
    setTripType(seed); setAmount(""); setAmtOk(false);
    setRemark(""); setROk(false); setError("");
    setTimeout(() => firstInputRef.current?.focus(), 20);
  }, [open, requisition?.ref_code, requisition?.form_data?.tripType]);

  if (!open || !requisition) return null;

  const trimmedAmt = amount.trim();
  const canSubmit = !busy && tripType && trimmedAmt && amtOk && rOk;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(
        `/requisitions/${requisition.ref_code}/action`,
        {
          action: "transport_po_acknowledge",
          remarks: remark.trim() || `Acknowledged — ${trimmedAmt}`,
          po_trip_type_confirmed: tripType,
          po_amount: trimmedAmt,
          po_amount_confirmed: amtOk,
          po_remark: remark.trim(),
          po_remark_confirmed: rOk,
        },
      );
      toast.success(`Acknowledged — ${requisition.ref_code}`);
      onDone?.(data);
      onClose?.();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="transport-po-ack-modal"
    >
      <div
        className="w-full max-w-lg bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <Truck className="text-[#7a1f2b]" size={18} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Transport PO · Acknowledged — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {actingAsBanner && (
            <div className="text-xs text-[#7a1f2b] bg-[#faf5eb] border border-[#b48a45] rounded px-3 py-2 flex items-start gap-2"
                 data-testid="super-admin-acting-as-banner">
              <ShieldAlert size={14} className="mt-0.5 shrink-0 text-[#b48a45]" />
              <div>You are acting as <strong>{actingAsBanner}</strong>.</div>
            </div>
          )}
          <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#b48a45] px-3 py-2 rounded">
            Confirm the trip type, the amount quoted by the vendor and tick
            both checkboxes before submitting the acknowledgement.
          </div>
          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded" data-testid="modal-server-error">
              {error}
            </div>
          )}
          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Trip Type (Confirmed) <span className="text-[#b3261e]">*</span>
            </span>
            <select
              ref={firstInputRef}
              value={tripType}
              onChange={(e) => setTripType(e.target.value)}
              className="input-vgws"
              data-testid="po-trip-type"
            >
              <option value="One Way">One Way</option>
              <option value="Round Trip">Round Trip</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Amount <span className="text-[#b3261e]">*</span>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">free text</span>
            </span>
            <div className="relative">
              <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6e7770]" />
              <input
                type="text"
                value={amount}
                onChange={(e) => setAmount(e.target.value.slice(0, 200))}
                placeholder="e.g. ₹ 2,500 or Rs 2500 approx"
                className="input-vgws pl-9"
                data-testid="po-amount"
              />
            </div>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={amtOk}
              onChange={(e) => setAmtOk(e.target.checked)}
              className="mt-1"
              data-testid="po-amount-confirmed"
            />
            <span>I confirm the amount entered above is correct.</span>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Remark <span className="text-[#6e7770] text-xs font-normal">(optional)</span>
            </span>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value.slice(0, 1000))}
              rows={3}
              className="input-vgws min-h-[80px]"
              placeholder="Vendor name, booking details, cancellation terms…"
              data-testid="po-remark"
            />
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={rOk}
              onChange={(e) => setROk(e.target.checked)}
              className="mt-1"
              data-testid="po-remark-confirmed"
            />
            <span>I have reviewed the remark (or no remark is required) and I am ready to submit the acknowledgement.</span>
          </label>
        </div>
        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost" data-testid="po-ack-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm bg-[#2d6b6b] hover:bg-[#1c4a4a] border-[#1c4a4a] ${!canSubmit ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="po-ack-confirm"
          >
            {busy ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 size={16} />}
            {busy ? "Submitting…" : "Acknowledged"}
          </button>
        </div>
      </div>
    </div>
  );
}
